import { DamperBlades, UserInput } from 'src/actuators/user-input/types.ts';
import { Decimal } from 'decimal.js-light';
import { AttributeUniqValues } from 'src/generated/operations.ts';
import { ActuatorTypes, QueryParams } from 'src/actuators/user-input/calculations/constants.ts';

export const damperBladesFactor: Record<DamperBlades, number> = {
    [DamperBlades.ParallelBladeWithSeals]: 8.51,
    [DamperBlades.OpposedBladeWithSeals]: 6.08,
    [DamperBlades.ParallelBladeWithoutSeals]: 4.86,
    [DamperBlades.OpposedBladeWithoutSeals]: 3.65,
};

export const getCorrectionFactor = (width: string, height: string): number => {
    if (width.length > 0 && height.length > 0) {
        const currWidth = new Decimal(width).dividedBy(1000);
        const currHeight = new Decimal(height).dividedBy(1000);
        if (currHeight.dividedBy(currWidth.toNumber()).lessThanOrEqualTo(1)) {
            return 1;
        }

        // Adjust the factors for a decimal place of 2. Each 0.01 of width/height is equal to a factor value of below
        const heightFactor = new Decimal(0.0120714285); // value of each 0.01m in height
        const widthFactor = new Decimal(0.0139285715); // value of each 0.01m in width
        const fixedPoint = new Decimal(3.00); // 0.4 width at 2m length, baseline reference point

        if (currWidth.greaterThanOrEqualTo(0.4) && currWidth.lessThan(2) &&
            currHeight.greaterThan(0.4) && currHeight.lessThanOrEqualTo(2)) {
            // Calculate the difference between the baseline of 0.4 and the entered value
            const widthDiff = currWidth.minus(0.4);
            // Calculate the offset value per 0.01m in width
            const widthCalc = widthDiff.mul(100).mul(widthFactor.toNumber());
            // Calculate the base height value for the height from the baseline minus the width offset value
            const heightBase = fixedPoint.minus(widthCalc.toNumber());
            // Calculate the height difference between 2 and the entered value
            const heightDiff = new Decimal(2).minus(currHeight);
            // Calculate the correction value from the base height value minus the height offset value
            const heightCalc = heightBase.minus(heightDiff.mul(100).mul(heightFactor));

            if (heightCalc.lessThan(1)) {
                return 1;
            }

            // Round to 2 decimal places
            return new Decimal(heightCalc.toFixed(2)).toNumber();
        }
    }
    return 1;
};

export const NumberPart = /^\s*([^a-zA-Z\s]+).*/gm;

export const handleSquareEu = (
    torques: AttributeUniqValues,
    searchParams: URLSearchParams,
    inputState: UserInput,
    type: ActuatorTypes,
) => {
    if (inputState.blade === null) {
        return '';
    }
    try {
        const areaDecimal = new Decimal(inputState.areaOrDiameter);
        const correctionFactor = getCorrectionFactor(inputState.width, inputState.height);
        const safetyFactor = (new Decimal(inputState.safety).dividedBy(100)).plus(1);
        const crankFactor = type === ActuatorTypes.Linear ? new Decimal(2)
            .dividedBy(new Decimal(inputState.crankArmLength).dividedBy(1000))
            .dividedBy(0.0254)
            .squareRoot() : new Decimal(1);

        const torque = areaDecimal
            .mul(damperBladesFactor[inputState.blade])
            .mul(correctionFactor)
            .mul(safetyFactor)
            .mul(crankFactor);

        const filtered = torques.values
            .map(({ value }) => {
                const curr = new Decimal(value.replace(NumberPart, '$1'));
                return {
                    value,
                    curr,
                    distance: curr.minus(torque),
                };
            })
            .filter(({ distance }) => distance.greaterThan(0))
            .sort((a, b) => a.distance.cmp(b.distance));

        if (filtered.length > 0) {
            searchParams.set(
                type === ActuatorTypes.Linear ? QueryParams.forces : QueryParams.torques,
                JSON.stringify(filtered.slice(0, 2).map(({ value }) => value)),
            );
        } else {
            searchParams.delete(QueryParams.forces);
            searchParams.delete(QueryParams.torques);
        }
        return torque.toFixed(2);
    } catch {
        return '';
    }
};
